<template>
  <!-- eslint-disable vue/no-parsing-error -->
  <div v-if="typesLoaded">
    <TableContractsFilters
      :team-members="teamMembers"
      :recruiters="recruiters"
      :contract-type-options="contractTypeOptions"
      @filters-updated="filterUpdated"
    />

    <ModalConfirmSwal
      v-if="isEndingJobsModalOpen"
      :header-text="$t('Are you sure?')"
      :text="$t('Do you want to end the selected jobs?')"
      :confirm-button-text="$t('Yes, end the jobs')"
      :processing-text="$t('Ending the jobs')"
      @confirmed="endJobs"
      @cancelled="cancelEndJobs"
    />
    <ModalRemindToBill
      v-if="isOpenModalBulkRemindToBill"
      :hire-ids="selectedRowsForModalBulkReminder"
      :is-open="isOpenModalBulkRemindToBill"
      :default-message="$store.state.tableBills.defaultBillReminderText"
      class="text-left f-14"
      bulk-reminder
      @closed="closeModalBulkRemindToBill"
    />
    <div class="card card-white">
      <BaseDataTable
        v-if="filterReady"
        multi-select
        :entries="tableData.rows"
        :columns="tableData.columns"
        :loading="$apollo.loading"
        :paginator-info="paginator"
        class="f-12"
        max-height="72vh"
        @search="search"
        @rows-selected="selectedRows = $event"
      >
        <template #additional-control-bar-elements>
          <WdsMenu z-index="10" global>
            <template #trigger>
              <BaseButton
                data-test="contracts-table-actions-button"
                class="!tw-ml-2"
                size="sm"
                :disabled="!hasSelectedRows"
              >
                {{ $t('Actions') }}
              </BaseButton>
            </template>
            <WdsMenuOption
              data-test="bulk-remind-to-submit-bill-button"
              @click="openModalBulkRemindToBill"
            >
              <WdsIcon :size="16" name="send-alt" />
              {{ $t('Remind to submit bill') }}
            </WdsMenuOption>
            <WdsMenuDivider />
            <WdsMenuOption theme="DESTRUCTIVE" @click="endAllSelectedJobs">
              <WdsIcon :size="16" name="trash-can" />
              {{ $t('End jobs') }}
            </WdsMenuOption>
          </WdsMenu>
        </template>
        <template #Action="{ row }">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <ProvideStateTriggeredBySlotProps
            v-slot="{
              isOn: isOpenModalRemindToBill,
              turnOff: closeModalRemindToBill,
              turnOn: openModalRemindToBill,
            }"
          >
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <ModalRemindToBill
              v-if="isOpenModalRemindToBill"
              :hire-ids="[row.localId]"
              :job-name="row.job.name"
              :freelancer-name="row.freelancer.name"
              :is-open="isOpenModalRemindToBill"
              :default-message="$store.state.tableBills.defaultBillReminderText"
              class="text-left f-14"
              @reminder-sent="handleBillReminderSent(row.localId)"
              @closed="closeModalRemindToBill"
            />
            <ButtonDropdown
              fixed
              no-caret
              :data-test="'contract-table-contract-row-actions-' + row.localId"
            >
              <i class="zmdi zmdi-more"></i>
              <template #dropdown>
                <div class="dropdown open">
                  <ul class="dropdown-menu dropdown-menu-right">
                    <li v-if="row.canRemindFreelancerForBilling">
                      <a
                        :data-test="'contract-table-contract-row-remind-to-bill-' + row.localId"
                        href="#"
                        @click="openModalRemindToBill"
                      >
                        {{ $t('Remind to submit bill') }}
                      </a>
                    </li>
                    <li v-if="row.canRemindFreelancerForBilling" role="separator" class="divider" />
                    <li v-if="threadIdFrom(row)">
                      <a :href="`/messages/${threadIdFrom(row)}?managecontract=1`">
                        {{ $t('Manage contract') }}
                      </a>
                    </li>
                    <li
                      v-if="threadIdFrom(row)"
                      :title="$t('Find bills, messages, reviews and more in the conversation.')"
                      data-toggle="tooltip"
                    >
                      <a :href="`/messages/${threadIdFrom(row)}`">
                        {{ $t('Go to conversation') }}
                      </a>
                    </li>
                    <li>
                      <a :href="`/contract/${row.localId}/pdf`" target="_blank">
                        {{ $t('Download contract PDF') }}
                      </a>
                    </li>
                    <li v-if="account.isIr35Applicable && row.sds">
                      <a
                        :href="`/sds/${row.sds.localId}/pdf`"
                        :title="$t('Download SDS as PDF')"
                        target="_blank"
                      >
                        {{ $t('Download Status Determination Statement') }}
                      </a>
                    </li>
                    <li v-if="row.bills.data.length > 0 && threadIdFrom(row)">
                      <a :href="`/messages/${threadIdFrom(row)}?show_feedback_modal=1`">
                        {{ $t('Review freelancer') }}
                      </a>
                    </li>
                    <li v-if="!row.job.completed">
                      <a
                        :data-swaltext="
                          $t(
                            'Do you want to terminate this job and terminate any ongoing job process?',
                          )
                        "
                        :data-swalconfirmtext="$t('Yes, mark the job as completed')"
                        :data-swalcompletedtext="$t('The job closes now')"
                        :data-formid="`form-job-close-${row.job.localId}`"
                        href="#"
                        class="swal-delete"
                      >
                        {{ $t('End job') }}
                      </a>
                      <BaseHtmlForm
                        :id="`form-job-close-${row.job.localId}`"
                        :action="`/job/${row.job.localId}/close`"
                        method="PATCH"
                        hidden
                        append-user-and-account
                      />
                    </li>
                    <li v-if="account.hasActiveWebhooks && !row.hasDraftContract">
                      <a
                        :data-swaltext="
                          $t('Are you sure you want to trigger Hire Accepted webhook?')
                        "
                        :data-swalconfirmtext="$t('Yes')"
                        :data-swalcompletedtext="$t('Webhook triggered.')"
                        :data-formid="`form-trigger-webhook-${row.localId}`"
                        href="#"
                        class="swal-delete"
                      >
                        {{ $t('Trigger webhook') }}
                      </a>
                      <BaseHtmlForm
                        :id="`form-trigger-webhook-${row.localId}`"
                        :action="`/integrations/webhook/hireAccepted/trigger/${row.localId}`"
                        method="POST"
                        hidden
                        append-user-and-account
                      />
                    </li>
                  </ul>
                </div>
              </template>
            </ButtonDropdown>
          </ProvideStateTriggeredBySlotProps>
        </template>
        <template #Name="{ row }">
          <TdFreelancer
            :global-id="row.freelancer?.globalId"
            :profile-url="row.freelancer?.primaryProfile?.url"
            :has-draft-contract="!!row.hasDraftContract"
          >
            <template #name>
              <WorkerName
                :name="row.freelancer?.name || $t('(No name)')"
                :manages-workers="row.recruiterManagesWorkers"
                :recruiter-name="row.recruiter?.name"
                :recruiter-fee-percentage="row.recruiterFeeInPercent"
                :recruiter-ownership-start-date="row.recruiterOwnershipStartDate"
                :recruiter-ownership-days="row.recruiterOwnershipDays"
                :recruiter-ownership-days-left="row.recruiterOwnershipDaysLeft"
              />
            </template>
          </TdFreelancer>
        </template>
        <template #Contract="{ row }">
          <PopoverContract :local-id="row.localId" />
        </template>
        <template #Recruiter="{ row }">
          <template v-if="row.hasRecruiter">
            <GhostComponent
              :title="`${$t('Staffing agency for contract')} #${row.localId}`"
              component-name-when-active="PopperCardButton"
              slot-to-render-when-inactive="btn"
              placement="top"
              strategy="absolute"
              global
            >
              <template #btn>
                <WdsLabel
                  sm
                  subtle
                  :error="row.recruiterOwnershipDaysLeft <= 0"
                  :success="row.recruiterOwnershipDaysLeft > 0"
                  class="m-r-5"
                >
                  {{ row.recruiterOwnershipDaysLeft <= 0 ? $t('Expired') : $t('Active') }}
                </WdsLabel>
              </template>

              <template #popper>
                <div class="m-b-10">
                  <strong>{{ row.recruiter.name }}</strong>
                </div>

                <div v-if="!row.recruiterManagesWorker" class="row">
                  <div class="col-sm-5 nowrap">
                    <strong>{{ $t('Fee') }}:</strong>
                  </div>
                  <div class="col-sm-7 nowrap">{{ row.recruiterFeeInPercent }} %</div>
                </div>
                <div class="row">
                  <div class="col-sm-5 nowrap">
                    <strong>{{ $t('Ownership Days') }}:</strong>
                  </div>
                  <div class="col-sm-7 nowrap">
                    {{ row.recruiterOwnershipDays }}
                    {{ row.recruiterOwnershipDays > 1 ? $t('days') : $t('day') }}
                  </div>
                </div>
                <div class="row">
                  <div class="col-sm-5 nowrap">
                    <strong>{{ $t('Started') }}:</strong>
                  </div>
                  <div class="col-sm-7 nowrap">
                    {{ moment(row.recruiterOwnershipStartDate).format('MMM DD, Y') }}
                  </div>
                </div>
                <div class="row">
                  <div class="col-sm-5 nowrap">
                    <strong>{{ $t('Status') }}:</strong>
                  </div>
                  <div class="col-sm-7 nowrap">
                    <template v-if="row.recruiterOwnershipDaysLeft <= 0">
                      {{ $t('Expired') }}
                    </template>
                    <template v-else>
                      <span v-if="row.recruiterOwnershipDaysLeft > 1">
                        {{
                          $t('Expires in :count days', {
                            count: row.recruiterOwnershipDaysLeft,
                          })
                        }}
                      </span>
                      <span v-else>
                        {{
                          $t('Expires in :count day', {
                            count: row.recruiterOwnershipDaysLeft,
                          })
                        }}
                      </span>
                    </template>
                  </div>
                </div>
              </template>
            </GhostComponent>
          </template>
          <span v-else>-</span>
        </template>
        <template #UkWorkerClassification="{ row }">
          <div v-if="row.ir35StatusForDisplay">
            <div
              v-if="row.ir35StatusForDisplay === 'OUTSIDE'"
              v-tooltip:top="$t('Gross payouts')"
              class="f-11 ucase f-700 c-black"
            >
              Outside IR35
            </div>
            <div
              v-else-if="row.ir35StatusForDisplay === 'INSIDE'"
              v-tooltip:top="$t('On payroll')"
              class="f-11 ucase f-700 text-warning"
            >
              Inside IR35
            </div>
            <template v-else-if="row.ir35StatusForDisplay === 'NEW'">Awaiting acceptance</template>
          </div>
          <div v-else-if="row.ukSoleTraderClassificationStatusForDisplay">
            <div
              v-if="row.ukSoleTraderClassificationStatusForDisplay === 'OUTSIDE'"
              v-tooltip:top="$t('Gross payouts')"
              class="f-11 ucase f-700 c-black"
            >
              Self-Employed Sole Trader
            </div>
            <div
              v-else-if="row.ukSoleTraderClassificationStatusForDisplay === 'INSIDE'"
              v-tooltip:top="$t('On payroll')"
              class="f-11 ucase f-700 text-warning"
            >
              Worksome Payroll
            </div>
            <template v-else-if="row.ukSoleTraderClassificationStatusForDisplay === 'NEW'">
              Awaiting acceptance
            </template>
          </div>
          <div v-else>-</div>
        </template>
        <template #UsWorkerClassification="{ row }">
          <div v-if="row.usWorkerClassificationStatusForDisplay">
            <div
              v-if="row.usWorkerClassificationStatusForDisplay === 'OUTSIDE'"
              v-tooltip:top="$t('Gross payouts')"
              class="f-11 ucase f-700 c-black"
            >
              IC (1099)
            </div>
            <div
              v-else-if="row.usWorkerClassificationStatusForDisplay === 'INSIDE'"
              v-tooltip:top="$t('On payroll')"
              class="f-11 ucase f-700 text-warning"
            >
              Employee (W-2)
            </div>
            <template v-else-if="row.usWorkerClassificationStatusForDisplay === 'NEW'">
              Awaiting acceptance
            </template>
          </div>
          <div v-else>-</div>
        </template>
        <template #GlobalWorkerClassification="{ row }">
          <div v-if="row.globalWorkerClassificationStatusForDisplay">
            <div
              v-if="row.globalWorkerClassificationStatusForDisplay === 'OUTSIDE'"
              v-tooltip:top="$t('Gross payouts')"
              class="f-11 ucase f-700 c-black"
            >
              Contractor
            </div>
            <div
              v-else-if="row.globalWorkerClassificationStatusForDisplay === 'INSIDE'"
              v-tooltip:top="$t('On payroll')"
              class="f-11 ucase f-700 text-warning"
            >
              Employee
            </div>
            <template v-else-if="row.globalWorkerClassificationStatusForDisplay === 'NEW'">
              Awaiting acceptance
            </template>
          </div>
          <div v-else>-</div>
        </template>
        <template #UsWorkerClassificationOverridden="{ row }">
          <template v-if="row.sds">
            <div v-if="row.sds.overridesAnotherSds">Yes</div>
            <div v-else>No</div>
          </template>
          <div v-else>-</div>
        </template>
        <template #ElapsedTime="{ row }">
          <template v-if="row.elapsedTime">{{ row.elapsedTime }} {{ $t('days') }}</template>
          <span v-else>-</span>
        </template>
        <template #LatestBill="{ row }">
          <span
            v-tooltip.top="
              (row.lastRemindedFreelancerToSubmitBill &&
                moment().diff(moment(row.lastRemindedFreelancerToSubmitBill), 'days') < 3) ||
              // or if we have sent reminder since the last refetching of query data
              $store.state.tableBills.hiresWithRecentBillReminders.includes(row.localId)
                ? $t('Reminded less than :count days ago', { count: 2 })
                : ''
            "
          >
            <template v-if="row.bills.data.length > 0">
              <span class="tw-whitespace-nowrap">
                {{ moment(row.bills.data[0]['createdAt']).format('Y-MM-DD') }}
              </span>
            </template>
            <i
              v-if="
                (row.lastRemindedFreelancerToSubmitBill &&
                  moment().diff(moment(row.lastRemindedFreelancerToSubmitBill), 'days') < 3) ||
                // or if we have sent reminder since the last refetching of query data
                $store.state.tableBills.hiresWithRecentBillReminders.includes(row.localId)
              "
              class="fa fa-clock-o"
              aria-hidden="true"
            ></i>
            <template v-if="row.bills.data.length > 0">
              <br />
              {{
                $t(':count day(s) ago', {
                  count: moment().diff(moment(row.bills.data[0]['createdAt']), 'days'),
                })
              }}
            </template>
          </span>
        </template>
        <template #Bills="{ row }">
          <span
            v-if="row.bills.data.length > 0"
            :class="`label label-${row.hasUnpaidInvoices ? 'warning' : 'success'}`"
            :title="`${
              row.hasUnpaidInvoices
                ? $t('Some bills are open for payment')
                : $t('All bills are paid')
            }`"
            data-toggle="tooltip"
          >
            {{ row.bills.data.length }}
            {{ $t('bills') }}
          </span>
          <span
            v-else
            :title="$t('The freelancer has not created a bill yet')"
            class="label label-quiet faded"
            data-toggle="tooltip"
          >
            {{ $t('No bills') }}
          </span>
        </template>

        <template #Rate="{ row }">
          <template v-if="row.rate > 0">
            {{ row.currency }}&nbsp;{{ row.rateFormatted }}
            <span :title="$t(row.ratetype || '?')" class="c-gray">
              {{ $t(row.ratetype || '?') }}
            </span>
          </template>
          <span v-else class="c-gray-700 f-12 f-300">{{ $t('No rate') }}</span>
        </template>
      </BaseDataTable>
    </div>
  </div>
</template>

<script>
/* eslint-disable import/extensions,no-restricted-syntax */
import moment from 'moment'
import gql from 'graphql-tag'
import { markRaw, nextTick } from 'vue'
import { WdsLabel, WdsIcon, WdsMenu, WdsMenuDivider, WdsMenuOption } from '@worksome/wds-components'
import TableContractsFilters from '@/components/TableContractsFilters.vue'
import HashMixin from '@/mixins/MethodHash'
import { TableDataHandler } from '@/vue-prop-types/DataTable'
import { stringToEnum, cleanStrict } from '@/utils/tools'
import { TABLE_BILLS_MUTATIONS } from '@/state/mutation-types'
import BaseDataTable from '@/components/BaseDataTable'
import { TdDate, TdText, TdFreelancer, TdJob } from '@/components/BaseDataTable/Cells'
import ButtonDropdown from '@/components/ButtonDropdown.vue'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import GhostComponent from '@/components/GhostComponent.vue'
import PopoverContract from '@/components/PopoverContract.vue'
import { LocalStorageEncoder, queryDecodeObject } from '@/utils/Encoders'
import ModalConfirmSwal from '@/components/ModalConfirmSwal.vue'
import { NetworkError, Message } from '@/utils/notification'
import WorkerName from '@/components/WorkerName.vue'
import ModalRemindToBill from '@/components/ModalRemindToBill.vue'
import BaseButton from '@/components/BaseButton.vue'

export default {
  name: 'TableContracts',
  components: {
    WdsLabel,
    WdsMenuDivider,
    WdsIcon,
    WdsMenuOption,
    WdsMenu,
    BaseButton,
    PopoverContract,
    BaseDataTable,
    GhostComponent,
    BaseHtmlForm,
    ButtonDropdown,
    TableContractsFilters,
    ModalConfirmSwal,
    TdFreelancer,
    WorkerName,
    ModalRemindToBill,
  },
  mixins: [HashMixin],
  apollo: {
    data: {
      skip() {
        return !this.filterReady
      },
      query: gql`
        query ContractsTable(
          $filter: HiresFilterInput
          $order: [HiresOrderByClause!]
          $search: String
          $page: Int!
          $count: Int!
        ) {
          data: me {
            globalId
            activeAccount {
              globalId
              user {
                globalId
                localId
                name
              }
              ... on Company {
                settings {
                  default_message_remind_freelancer_to_bill
                }
              }
              ... on HasHires {
                hires(
                  filter: $filter
                  orderBy: $order
                  search: $search
                  first: $count
                  page: $page
                ) {
                  paginatorInfo {
                    ...PaginatorInfoFragment
                    count
                    currentPage
                    total
                  }
                  data {
                    globalId
                    localId
                    jobNameOnContract
                    rate
                    ratetype
                    rateFormatted
                    currency
                    canRemindFreelancerForBilling
                    hasUnpaidInvoices
                    createdAt
                    dateStart
                    dateEnd
                    poNumber
                    hasRecruiter
                    recruiterManagesWorkers
                    recruiterFeeInPercent
                    recruiterOwnershipDays
                    recruiterOwnershipDaysLeft
                    recruiterOwnershipStartDate
                    recruiterManagesWorkers
                    hasDraftContract
                    elapsedTime
                    lastRemindedFreelancerToSubmitBill
                    company {
                      globalId
                      name
                    }
                    recruiter {
                      globalId
                      name
                    }
                    threadId
                    freelancer {
                      globalId
                      name
                      primaryProfile {
                        globalId
                        url
                      }
                    }
                    job: yjob {
                      globalId
                      localId
                      name
                      completed
                    }
                    contractTypeShort
                    contractTypeDescription
                    ir35StatusForDisplay
                    ukSoleTraderClassificationStatusForDisplay
                    usWorkerClassificationStatusForDisplay
                    globalWorkerClassificationStatusForDisplay
                    sds {
                      globalId
                      localId
                      overridesAnotherSds
                    }
                    bills(first: 100000) {
                      data {
                        globalId
                        localId
                        issuedAt
                        createdAt
                      }
                    }
                  }
                }
              }
              ... on HasRecruiters {
                recruiters(first: 1000) {
                  data {
                    globalId
                    localId
                    name
                  }
                }
              }
              ... on HasTeamMembers {
                teamMembers(
                  first: 1000
                  orderBy: [{ column: NAME, order: ASC }]
                  isJobOwner: true
                ) {
                  data {
                    globalId
                    localId
                    name
                  }
                }
              }
            }
          }
        }
        ${BaseDataTable.fragments.pagination}
      `,
      variables() {
        return {
          filter: { ...this.filter },
          order: this.ordering,
          search: this.searchValue,
          page: this.paginationSettings.page,
          count: this.paginationSettings.displayPerPage,
        }
      },
    },
  },
  data() {
    return {
      typesLoaded: false,
      contractTypeOptions: [],
      filter: {},
      paginationSettings: {
        page: 1,
        displayPerPage: 50,
      },
      orderSettings: new LocalStorageEncoder({
        defaultVal: { createdAt: 'desc' },
        storageKey: 'ordering',
        decoder: queryDecodeObject,
        filter(data) {
          return cleanStrict({ ...data })
        },
      }),
      searchValue: '',
      header: [],
      currentUrl: this.getCurrentUrl(),
      selectedRows: {},
      isEndingJobsModalOpen: false,
      isOpenModalBulkRemindToBill: false,
    }
  },
  computed: {
    filterReady() {
      return Object.keys(this.filter).length > 0
    },
    moment: () => moment,
    paginator() {
      return this?.data?.activeAccount?.hires?.paginatorInfo
    },
    rows() {
      return this?.data?.activeAccount?.hires?.data || []
    },
    defaultBillReminderText() {
      return this?.data?.activeAccount?.settings?.default_message_remind_freelancer_to_bill || ''
    },
    selectedRowsForModalBulkReminder() {
      return Object.values(this.selectedRows).map((row) => row.localId)
    },
    teamMembers() {
      const teamMembers = this?.data?.activeAccount?.teamMembers?.data
      if (teamMembers) {
        return [
          {
            id: this?.data?.activeAccount?.user?.localId,
            name: this.$t('My own'),
          },
          ...teamMembers.map((teamMember) => ({
            id: teamMember.localId,
            name: teamMember.name,
          })),
        ]
      }
      return []
    },
    recruiters() {
      const recruiters = this?.data?.activeAccount?.recruiters?.data
      if (recruiters) {
        return recruiters.map((recruiter) => ({
          id: recruiter.localId,
          name: recruiter.name,
        }))
      }
      return []
    },
    tableData() {
      return new TableDataHandler({
        rows: this.rows,
        columns: this.header,
      })
    },
    totalElements() {
      return this?.data?.activeAccount?.hires?.paginatorInfo?.total || 0
    },
    ordering() {
      const o = []
      for (const key in this.orderSettings.data) {
        if (this.orderSettings.data[key]) {
          o.push({
            field: stringToEnum(key),
            order: stringToEnum(this.orderSettings.data[key]),
          })
        }
      }
      return o
    },
    where(value) {
      const cond = this.tableData.columns.filter((h) => h.orderAble)
      const first = cond.pop()
      if (value !== '') {
        return {
          column: stringToEnum(first.key),
          operator: 'LIKE',
          value: `%${this.searchValue}%`,
          OR: cond.map((c) => ({
            column: stringToEnum(c.key),
            operator: 'LIKE',
            value: `%${this.searchValue}%`,
          })),
        }
      }
      return null
    },
    hasSelectedRows() {
      return Object.keys(this.selectedRows).length > 0
    },
  },
  watch: {
    defaultBillReminderText(newValue) {
      this.$store.commit(TABLE_BILLS_MUTATIONS.SET_DEFAULT_BILL_REMINDER_TEXT, newValue)
    },
  },
  async mounted() {
    const availableContractTypes = await this.fetchContractTypes()

    this.contractTypeOptions = availableContractTypes

    this.typesLoaded = true
  },
  created() {
    this.header = [
      {
        title: 'Created',
        key: 'createdAt',
        class: 'nowrap',
        renderUsing: {
          component: markRaw(TdDate),
          componentProps: (row) => ({
            date: row.createdAt,
          }),
        },
      },
      {
        title: 'Name',
        key: 'freelancer',
        class: 'text-left',
        slot: 'Name',
        ignoreClickForRowSelection: true,
      },
      {
        title: 'Start date',
        key: 'dateStart',
        class: 'text-left f-13 nowrap',
        slot: 'Start',
        renderUsing: {
          component: markRaw(TdDate),
          componentProps: (row) => ({
            date: row.dateStart,
          }),
        },
      },
      {
        title: 'End date',
        key: 'dateEnd',
        class: 'text-left f-13 nowrap',
        slot: 'End',
        renderUsing: {
          component: markRaw(TdDate),
          componentProps: (row) => ({
            date: row.dateEnd,
          }),
        },
      },
      {
        title: 'Active tenure',
        key: 'elapsedTime',
        class: 'text-left f-13',
        slot: 'ElapsedTime',
      },
      {
        title: 'Job',
        key: 'job',
        class: 'text-left',
        orderAble: false,
        renderUsing: {
          component: markRaw(TdJob),
          componentProps: (row) => ({
            localId: row.job?.localId,
            name: row.jobNameOnContract || row.job?.name || '',
            isCompleted: !!row.job?.completed,
            companyName: this.account?.isOrganization ? row.company?.name : '',
          }),
        },
        ignoreClickForRowSelection: true,
      },
      {
        title: 'PO no.',
        key: 'poNumber',
      },
      {
        title: 'Rate',
        key: 'rate',
        class: 'f-500 f-13 text-left',
      },
      {
        title: 'Recruiter',
        key: 'recruiter',
        class: 'nowrap c-gray f-13',
        orderAble: false,
        ignoreClickForRowSelection: true,
      },
      {
        title: 'Bills',
        key: 'bills',
        class: 'nowrap c-gray f-13',
        orderAble: false,
      },
      {
        title: 'Latest bill',
        key: 'latestBill',
        class: 'c-gray f-13',
        slot: 'LatestBill',
      },
      {
        title: 'Type',
        key: 'contractType',
        class: 'nowrap c-gray f-13',
        slot: 'ContractType',
        orderAble: true,
        renderUsing: {
          component: markRaw(TdText),
          componentProps: (row) => ({
            text: row.contractTypeShort,
            tooltip: row.contractTypeDescription,
          }),
        },
      },
      {
        title: 'Action',
        key: 'action',
        class: 'text-right',
        orderAble: false,
        ignoreClickForRowSelection: true,
      },
    ].map((h) => {
      /* eslint-disable-next-line no-param-reassign */
      h.order = this.orderSettings.data[h.key]
      return h
    })
    // Include the ir35 column if applicable in second-to-last place
    if (this.account.isIr35Applicable || this.account.isUkSoleTraderClassificationApplicable) {
      this.header.splice(this.header.length - 1, 0, {
        title: 'Classification',
        key: 'sds',
        class: 'nowrap c-gray f-13',
        orderAble: false,
        slot: 'UkWorkerClassification',
      })
    }
    if (this.account.isUsWorkerClassificationApplicable) {
      this.header.splice(
        this.header.length - 1,
        0,
        {
          title: 'Classification',
          key: 'sds',
          class: 'nowrap c-gray f-13',
          orderAble: false,
          slot: 'UsWorkerClassification',
        },
        {
          title: 'Overridden',
          key: 'sdsOverridden',
          class: 'nowrap c-gray f-13',
          orderAble: false,
          slot: 'UsWorkerClassificationOverridden',
        },
      )
    }
    if (this.account.isGlobalWorkerClassificationApplicable) {
      this.header.splice(this.header.length - 1, 0, {
        title: 'Classification',
        key: 'sds',
        class: 'nowrap c-gray f-13',
        orderAble: false,
        slot: 'GlobalWorkerClassification',
      })
    }

    this.header.splice(this.header.length - 1, 0, {
      title: 'Contract',
      key: 'id',
      class: 'text-left',
      ignoreClickForRowSelection: true,
    })
  },
  methods: {
    async fetchContractTypes() {
      const result = await this.$apollo.query({
        query: gql`
          query {
            availableContractTypesForCompany {
              name
              enum
            }
          }
        `,
      })

      return result.data.availableContractTypesForCompany
    },
    search(options) {
      this.paginationSettings.displayPerPage = options.perPage
      this.paginationSettings.page = options.page + 1
      this.orderSettings.data = options.order
      this.searchValue = options.search
    },
    filterUpdated($event) {
      this.currentUrl = this.getCurrentUrl()
      // Since the filter might fire at random, we only update our copy when the new filter is different from ours.
      // We also reset out page to one, since the amount of data we have might change such that there are fewer pages
      // for us to paginate.
      if (this.hash(this.filter) !== this.hash($event)) {
        this.filter = $event
        this.paginationSettings.page = 1
      }
    },
    getCurrentUrl() {
      return encodeURIComponent(window.location.pathname + window.location.search)
    },
    handleBillReminderSent(hireId) {
      $(`#${hireId}-actions-dropdown`).trigger('click.bs.dropdown')
    },
    openModalBulkRemindToBill() {
      this.isOpenModalBulkRemindToBill = true
    },
    closeModalBulkRemindToBill() {
      this.isOpenModalBulkRemindToBill = false
    },
    endAllSelectedJobs() {
      this.isEndingJobsModalOpen = true
    },
    threadIdFrom(row) {
      return row.threadId
    },
    async endJobs() {
      const mutationArguments = []
      const mutationFragments = []
      const mutationVariables = {}

      for (const [, row] of Object.entries(this.selectedRows)) {
        mutationArguments.push(`$input${row.job.localId}: EndJobInput!`)
        mutationFragments.push(
          `endJob${row.job.localId}: endJob(input: $input${row.job.localId}) { id }`,
        )
        mutationVariables[`input${row.job.localId}`] = {
          id: row.job.globalId,
          accountId: this.account.globalId,
        }
      }

      this.$apollo.client = 'publicApi'

      await this.$apollo
        .mutate({
          mutation: gql`
            mutation EndJob(${mutationArguments.join(',')}) {
              ${mutationFragments.join(',')}
            }
          `,
          variables: mutationVariables,
        })
        .then(() => {
          new Message($t('Selected jobs were ended.')).dispatch(this)
        })
        .catch((e) => {
          new NetworkError({
            msg: this.$t('Error occurred. The action was not completed'),
            ex: e,
          }).dispatch(this)
        })
        .finally(() => {
          this.$apollo.queries.data.refetch()
          this.isEndingJobsModalOpen = false
        })
    },
    cancelEndJobs() {
      nextTick(() => {
        this.$apollo.client = 'apolloClient'
        this.isEndingJobsModalOpen = false
      })
    },
  },
}
</script>

<style scoped lang="scss">
.label {
  font-size: 9px;
  display: inline-block !important;
  margin: 1px !important;
  padding: 0.3em 0.6em 0.3em !important;
}
</style>
